import { useTranslation } from 'react-i18next'
import { Divider, Popover, TextField } from '@mui/material'
import { MouseEvent, useEffect, useState } from 'react'

import { Pocket } from '../../utils/dexie/db'
import { numberWithCommas } from '../../utils'
import { countriesCurrencies } from '../../lib'
import SwapIcon from '../../assets/icons/swap-icon.svg'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import TimesIconRed from '../../assets/icons/times-icon-red.svg'
import CheckIconGreen from '../../assets/icons/check-icon-green.svg'
import EditIconGreen from '../../assets/icons/edit-square-green.svg'
import WalletIconGreen from '../../assets/icons/wallet-icon-green.svg'

export const PocketItem = ({
  pocket,
  showMoreActionsButton,
  handleUpdatePocketName,
  handleDeleteSeletedPocket,
  handleTransferSeletedPocket
}: {
  pocket: Pocket
  showMoreActionsButton: boolean
  handleDeleteSeletedPocket: (pocketIdToSet: string) => void
  handleTransferSeletedPocket: (pocketIdToSet: string) => void
  handleUpdatePocketName: (pocketIdToSet: string, pocketNameToSet: string) => void
}) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toUpperCase()

  const { t } = useTranslation(['pockets-management'])

  const [pocketData, setPocketData] = useState<Pocket | null>(null)
  const [pocketNameToSet, setPocketNameToSet] = useState<string>('')
  const [pocketNameSetted, setPocketNameSetted] = useState<string>('')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [errors, setErrors] = useState<{ pocketNameToSet: string } | null>(null)
  const [pocketNameEditionState, setPocketNameEditionState] = useState<boolean>(false)

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  useEffect(() => {
    setPocketData(pocket)
    setPocketNameSetted(String(pocket.name))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pocket])

  return (
    <div className="pocket-container">
      <div className="top-items">
        <div className="icon-container">
          <img
            src={WalletIconGreen}
            alt="wallet-icon-green"
          />
        </div>
        {showMoreActionsButton && (
          <button
            className="button-container"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              setAnchorEl(event.currentTarget)
            }}
          >
            ⋮
          </button>
        )}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <div
            className="notification-container"
            onClick={() => {
              handleTransferSeletedPocket(String(pocketData?.id))
              setAnchorEl(null)
            }}
          >
            <div className="notification-read-text pocket-more-button-option">
              <img
                src={SwapIcon}
                alt="swap-icon"
              />
              <div>{t('pocketActions.transfer')}</div>
            </div>
          </div>
          {/* pocketData && !pocketData.main && ( */}
          {!pocketData && (
            <>
              <Divider />
              <div
                className="notification-container"
                onClick={() => {
                  handleDeleteSeletedPocket(String((pocketData as unknown as Pocket)?.id))
                  setAnchorEl(null)
                }}
              >
                <div className="notification-read-text pocket-more-button-option">
                  <img
                    src={TrashIcon}
                    alt="trash-icon"
                  />
                  <div>{t('pocketActions.delete')}</div>
                </div>
              </div>
            </>
          )}
        </Popover>
      </div>
      <div className="balance-items-container">
        <div className="balance-items">{`$ ${numberWithCommas(Number(pocketData?.balance))} ${currency}`}</div>
        <div className="balance-subitems">
          <div>{`${t('pocketActions.availableBalance')}:`}</div> <div>{`$ ${numberWithCommas(Number(pocketData?.balance) - Number(pocketData?.balanceToBeDebited))} ${currency}`}</div>
        </div>
        <div className="balance-subitems">
          <div>{`${t('pocketActions.toBeDebited')}:`}</div> <div>{`$ ${numberWithCommas(Number(pocketData?.balanceToBeDebited))} ${currency}`}</div>
        </div>
        <div className="balance-subitems">
          <div>{`${t('pocketActions.toBeCredited')}:`}</div> <div>{`$ ${numberWithCommas(Number(pocketData?.balanceToBeCredited))} ${currency}`}</div>
        </div>
      </div>
      <div className="info-items-container">
        <div
          className="info-items"
          style={pocketNameEditionState ? { width: '80%' } : { width: '90%' }}
        >
          {pocketNameEditionState ? (
            <TextField
              fullWidth
              variant="standard"
              value={pocketNameToSet || ''}
              error={!!errors?.pocketNameToSet}
              helperText={errors?.pocketNameToSet}
              onChange={(event) => {
                setPocketNameToSet(event.target.value.toUpperCase())
              }}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            />
          ) : (
            <div className="info-items">{pocketData?.name || pocketNameSetted || '-'}</div>
          )}
        </div>
        <div
          className="button-edit-container"
          style={pocketNameEditionState ? { width: '20%' } : { width: '10%' }}
        >
          {pocketNameEditionState ? (
            <>
              <img
                src={CheckIconGreen}
                alt="check-icon-green"
                style={{
                  width: '24px',
                  height: '24px'
                }}
                onClick={async () => {
                  setErrors(null)

                  if (!pocketNameToSet) {
                    setErrors({ pocketNameToSet: t('errors.pocketNameToSet') })
                    return
                  }

                  await handleUpdatePocketName(String(pocketData?.id), String(pocketNameToSet))
                  setPocketNameEditionState(false)
                  setPocketNameSetted(String(pocketNameToSet))
                }}
              />
              <img
                src={TimesIconRed}
                alt="times-icon"
                style={{
                  width: '24px',
                  height: '24px'
                }}
                onClick={() => {
                  setPocketNameToSet(pocketNameSetted)
                  setPocketNameEditionState(false)
                }}
              />
            </>
          ) : (
            <img
              src={EditIconGreen}
              alt="edit-icon"
              onClick={() => {
                setPocketNameEditionState(true)
                setPocketNameToSet(String(pocketData?.name))
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
